/**
 * Notification service for Correly.
 *
 * Handles:
 * - Registering device tokens with Supabase
 * - Scheduling local reminders via Capacitor
 * - Managing notification preferences
 *
 * On native (iOS/Android), uses Capacitor PushNotifications plugin.
 * On web, uses Firebase Cloud Messaging.
 */

import { supabase } from './supabase';
import {
  getSupplementReminder,
  getMissedTaskMessage,
  getDailyLogReminder,
  isQuietHours,
} from '../utils/notifications';

/**
 * Safely check if running on a native Capacitor platform.
 * Returns false on web to avoid importing native-only modules.
 */
function isNative() {
  try {
    // Use globalThis to check for Capacitor without importing the module
    return !!(globalThis.Capacitor && globalThis.Capacitor.isNativePlatform && globalThis.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * Dynamic import helper that hides module paths from Vite's static analysis.
 * This prevents Vite from trying to resolve native-only Capacitor plugins during web builds.
 */
function nativeImport(pkg) {
  return new Function('pkg', 'return import(pkg)')(pkg);
}

/**
 * Register for push notifications on native platforms.
 * On web, uses Firebase (handled separately in firebase.js).
 */
export async function registerPushNotifications(userId) {
  if (!isNative()) {
    // Web — handled by firebase.js
    return null;
  }

  try {
    const { PushNotifications } = await nativeImport('@capacitor/push-notifications');

    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push notification permission denied');
      return null;
    }

    // Register with APNS/FCM
    await PushNotifications.register();

    // Listen for token
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push registration token:', token.value);

      // Store token in Supabase for server-side sending
      await saveDeviceToken(userId, token.value);
    });

    // Handle registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Handle received notifications (foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received in foreground:', notification);
      // Show an in-app toast or update UI
      showInAppNotification(notification);
    });

    // Handle notification tap (background → foreground)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push action performed:', action);
      // Navigate to relevant screen based on notification data
      handleNotificationAction(action.notification);
    });

    return true;
  } catch (err) {
    console.error('Failed to register push notifications:', err);
    return null;
  }
}

/**
 * Save device token to Supabase for server-side push.
 */
async function saveDeviceToken(userId, token) {
  // Upsert to a device_tokens table (you may need to create this)
  // For now, store in user metadata
  await supabase.auth.updateUser({
    data: { push_token: token },
  });
}

/**
 * Schedule local notifications for a client's supplements.
 * Called when supplements change or at the start of each day.
 *
 * @param {Array} supplements - active supplements with reminder_time
 */
export async function scheduleReminders(supplements) {
  if (!isNative()) return;

  try {
    const { LocalNotifications } = await nativeImport('@capacitor/local-notifications');

    // Cancel existing scheduled notifications
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel(pending);
    }

    const now = new Date();
    const notifications = [];

    supplements.forEach((supp, index) => {
      if (!supp.reminder_time || !supp.is_active) return;

      const [hours, minutes] = supp.reminder_time.split(':').map(Number);

      // Schedule the reminder
      const scheduleDate = new Date(now);
      scheduleDate.setHours(hours, minutes, 0, 0);

      // If time has passed today, schedule for tomorrow
      if (scheduleDate <= now) {
        scheduleDate.setDate(scheduleDate.getDate() + 1);
      }

      const message = getSupplementReminder(supp.name, supp.dosage || '');

      notifications.push({
        id: index + 1,
        title: 'Correly — Supplement Reminder',
        body: message,
        schedule: { at: scheduleDate, repeats: true, every: 'day' },
        sound: 'default',
        smallIcon: 'ic_notification',
        largeIcon: 'ic_launcher',
        extra: {
          supplementId: supp.id,
          type: 'supplement_reminder',
        },
      });

      // Schedule missed supplement follow-up (2 hours later)
      const missedDate = new Date(scheduleDate);
      missedDate.setHours(missedDate.getHours() + 2);

      // Don't schedule missed notification in quiet hours
      const missedHour = missedDate.getHours();
      if (missedHour < 22 && missedHour >= 7) {
        notifications.push({
          id: 1000 + index,
          title: 'Correly',
          body: getMissedTaskMessage(),
          schedule: { at: missedDate, repeats: true, every: 'day' },
          sound: 'default',
          extra: {
            supplementId: supp.id,
            type: 'missed_supplement',
          },
        });
      }
    });

    // Schedule daily wellness log reminder (default 8pm)
    const logReminderDate = new Date(now);
    logReminderDate.setHours(20, 0, 0, 0);
    if (logReminderDate <= now) {
      logReminderDate.setDate(logReminderDate.getDate() + 1);
    }

    notifications.push({
      id: 9999,
      title: 'Correly',
      body: getDailyLogReminder(),
      schedule: { at: logReminderDate, repeats: true, every: 'day' },
      sound: 'default',
      extra: { type: 'daily_log' },
    });

    // Schedule all
    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
    }
  } catch (err) {
    console.error('Failed to schedule notifications:', err);
  }
}

/**
 * Show an in-app notification toast (foreground).
 */
function showInAppNotification(notification) {
  // This can be enhanced with a proper toast component
  // For now, use a simple approach
  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
    new Notification(notification.title || 'Correly', {
      body: notification.body,
      icon: '/correly-icon.svg',
    });
  }
}

/**
 * Handle notification tap — route to relevant screen.
 */
function handleNotificationAction(notification) {
  const extra = notification.extra || notification.data || {};

  switch (extra.type) {
    case 'supplement_reminder':
    case 'missed_supplement':
      // Navigate to Supplements view
      window.location.hash = '/client/supplements';
      break;
    case 'daily_log':
      // Navigate to wellness log
      window.location.hash = '/client/log';
      break;
    case 'message':
      // Navigate to messages
      window.location.hash = '/client/messages';
      break;
    default:
      break;
  }
}
